export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  message: string;
  action?: ToastAction;
}

export const TOAST_DURATION_MS = 6000;

interface Timers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => {
    globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>);
  },
};

/** Transient notifications, e.g. "Deleted Pain — Undo" (SPEC §6.6). */
export class Toasts {
  items: Toast[] = $state.raw([]);
  #next = 1;
  readonly #timers: Timers;
  // Timer bookkeeping only; deliberately not reactive.
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  readonly #handles = new Map<number, unknown>();

  constructor(timers: Timers = defaultTimers) {
    this.#timers = timers;
  }

  show(message: string, options: { action?: ToastAction; duration?: number } = {}): number {
    const id = this.#next++;
    const toast: Toast = options.action ? { id, message, action: options.action } : { id, message };
    this.items = [...this.items, toast];
    this.#handles.set(
      id,
      this.#timers.setTimeout(() => {
        this.dismiss(id);
      }, options.duration ?? TOAST_DURATION_MS),
    );
    return id;
  }

  dismiss(id: number): void {
    const handle = this.#handles.get(id);
    if (handle !== undefined) this.#timers.clearTimeout(handle);
    this.#handles.delete(id);
    this.items = this.items.filter((toast) => toast.id !== id);
  }

  /** Runs a toast's action and dismisses it. */
  act(id: number): void {
    const toast = this.items.find((t) => t.id === id);
    this.dismiss(id);
    toast?.action?.run();
  }
}
